import useAccessBadge from './useAccessBadge';

/**
 * The organization's student sign-in policy, read from the access badge.
 *
 * Three surfaces need this — the designer's lesson settings, the live game's
 * settings menu, and the menu's own visibility check — and they all write the
 * same `settings.requireStudentSso` on the lesson. When they disagreed about the
 * policy, one showed a locked toggle while another let a teacher change it, so
 * they derive it from here rather than each reading the badge themselves.
 *
 * Previously this came from `useOrganizationSsoSettings`, i.e. from
 * `GET /v0.5/organizations/:orgId/settings` — an Admin-only endpoint. That meant
 * a regular teacher could never see their own org's policy and always got the
 * unlocked default. The badge is fetched for everyone, so that blind spot is gone.
 *
 * Note what this deliberately does NOT return: `requireStudentSsoDefault`. The
 * org's default is applied server-side when a lesson is created, and the lesson's
 * own stored value is the only truth afterwards — see the comment on the badge
 * type for what goes wrong if a caller reintroduces it.
 */
function useStudentSsoPolicy() {
  const { accessBadge, isFetched } = useAccessBadge();
  // `settings` is documented as always present, but it is newer than the clients
  // reading it: a badge from an older backend has no `settings` at all, and a
  // failed badge request has no organization either. Absent means "we don't
  // know", which for both of these is the same as "off" — so on a backend that
  // hasn't shipped `isStudentSsoEnabled` yet the toggle simply stays hidden,
  // exactly as it behaved before this field existed.
  const settings = accessBadge?.organization?.settings;

  return {
    // Whether students are able to sign in at all — the org has at least one
    // provider with `allowStudents`. This is capability, not preference: an org
    // can be capable while its default join method is still "type your name".
    // backend: organization.settings.isStudentSsoEnabled
    orgHasStudentSsoProviders: !!settings?.isStudentSsoEnabled,
    // Whether sign-in is checked at join time for every lesson, existing ones
    // included. When true the per-lesson toggle shows on and locked.
    orgEnforcesStudentSsoForAllLessons: !!settings?.enforceStudentSsoForAllLessons,
    // True once the badge has resolved. Callers that show plan-dependent copy
    // need it: until then "no settings" means unknown, not off.
    isBadgeFetched: isFetched,
  };
}

/**
 * Whether to offer the per-lesson "Require Student SSO" control at all.
 *
 * Shared because the same three-term expression governs both the designer and
 * the live-game menu, and only the designer's copy has ever had tests. The two
 * disagreeing is the original reason this module exists.
 *
 * Each term earns its place:
 *  - capability — the org can actually serve a sign-in, so the teacher may ask
 *    for one. This alone is the fix for capable-but-not-defaulted orgs.
 *  - enforcement — reachable *without* capability, by turning "Allow students"
 *    off on the last provider while enforce is on, or by district inheritance.
 *    Hiding the row there would hide it exactly when the teacher most needs to
 *    be told sign-in is locked on.
 *  - the lesson's own value — a lesson that already requires sign-in must stay
 *    switchable off after the org changes its mind, or the setting keeps being
 *    enforced at join time with no control anywhere to clear it.
 */
export function shouldOfferStudentSsoToggle({
  orgHasStudentSsoProviders,
  orgEnforcesStudentSsoForAllLessons,
  lessonRequiresStudentSso,
}: {
  orgHasStudentSsoProviders: boolean;
  orgEnforcesStudentSsoForAllLessons: boolean;
  lessonRequiresStudentSso: boolean;
}): boolean {
  return (
    orgHasStudentSsoProviders ||
    orgEnforcesStudentSsoForAllLessons ||
    lessonRequiresStudentSso
  );
}

export default useStudentSsoPolicy;
